import { ENVS } from "config/envs";
import { getRegionalConsentDefault, type RegionalConsentDefault } from "./regional-consent";

export const COOKIE_CONSENT_KEY = "rivetai_cookie_consent";

export type CookieConsentValue = "all" | "essential";

export const getCookieConsent = (): CookieConsentValue | null => {
  if (typeof window === "undefined") return null;
  const stored = window.localStorage.getItem(COOKIE_CONSENT_KEY);
  if (stored === "all" || stored === "essential") {
    return stored;
  }
  return null;
};

/**
 * Consent assumed for a visitor with no stored choice. Only when REACT_APP_REGIONAL_CONSENT is
 * "true" and the visitor is outside Europe; otherwise null and nothing changes.
 */
export const getDefaultConsent = (): RegionalConsentDefault | null => {
  if (typeof window === "undefined" || getCookieConsent() !== null) return null;
  return getRegionalConsentDefault(ENVS.regionalConsent);
};

export const hasAnalyticsConsent = (): boolean =>
  getCookieConsent() === "all" || getDefaultConsent()?.analytics === true;

/** Ad measurement (Google Ads, OpenAI ads pixel). Denied by default under Global Privacy Control. */
export const hasAdsConsent = (): boolean =>
  getCookieConsent() === "all" || getDefaultConsent()?.ads === true;

const toGtagState = (granted: boolean) => (granted ? "granted" : "denied");

/**
 * Tells Google Consent Mode about the regional default. index.html starts everything denied
 * and only upgrades a stored "all", so this runs once at boot for visitors with no choice.
 */
export const applyDefaultConsentToGtag = (): void => {
  const defaults = getDefaultConsent();
  if (!defaults) return;
  const ads = toGtagState(defaults.ads);
  window.gtag?.("consent", "update", {
    analytics_storage: toGtagState(defaults.analytics),
    ad_storage: ads,
    ad_user_data: ads,
    ad_personalization: ads,
  });
};

export const setCookieConsent = (value: CookieConsentValue): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(COOKIE_CONSENT_KEY, value);
  const consent = value === "all" ? "granted" : "denied";
  window.gtag?.("consent", "update", {
    ad_storage: consent,
    analytics_storage: consent,
    ad_user_data: consent,
    ad_personalization: consent,
  });
  window.dispatchEvent(new CustomEvent("rivetai:cookie-consent", { detail: value }));
};
